'use client';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchFavorites, toggleFavorite } from '@/lib/api/favoritesApi';
import {
  FavoritesState,
  FavoritesStateSchema,
  ToggleFavoritePayload,
} from '@/lib/schemas/favorites';

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------
export const favoritesKeys = {
  all: (customerId: string) => ['favorites', customerId] as const,
};

// ---------------------------------------------------------------------------
// Normalise the flat array from the API into { products, designs } maps
// ---------------------------------------------------------------------------
function normaliseFavorites(items: any[]): FavoritesState {
  const state: FavoritesState = { products: {}, designs: {} };
  for (const item of items) {
    if (item.type === 'product') {
      state.products[item.itemId] = { ...item, isFavorite: true };
    } else if (item.type === 'design') {
      state.designs[item.itemId] = { ...item, isFavorite: true };
    }
  }
  return FavoritesStateSchema.parse(state);
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UseFavoritesOptions {
  /** Pass undefined / null / '' when the user is not logged in. */
  customerId: string | null | undefined;
  /** Override staleTime (default: 5 minutes). */
  staleTime?: number;
}

/**
 * Returns the current favorites state and a `toggleFavorite` mutation.
 *
 * Replaces: `fetchFavoritesThunk`, `toggleFavoriteThunk`, and `favoriteSlice`.
 */
export function useFavorites({ customerId, staleTime = 5 * 60 * 1000 }: UseFavoritesOptions) {
  const queryClient = useQueryClient();

  // -------------------------------------------------------------------------
  // Query — fetch all favorites for this customer
  // -------------------------------------------------------------------------
  const query = useQuery<FavoritesState, Error>({
    queryKey: favoritesKeys.all(customerId ?? ''),
    queryFn: async () => {
      const items = await fetchFavorites(customerId!);
      return normaliseFavorites(items);
    },
    enabled: !!customerId,
    staleTime,
  });

  const favorites: FavoritesState = query.data ?? { products: {}, designs: {} };

  // -------------------------------------------------------------------------
  // Mutation — toggle a single favorite with optimistic update
  // -------------------------------------------------------------------------
  const mutation = useMutation<
    { itemId: string; isFavorite: boolean; type: string; itemAdditionalData?: any },
    Error,
    ToggleFavoritePayload,
    { previousFavorites: FavoritesState | undefined }
  >({
    mutationFn: (payload) => toggleFavorite(payload),

    // Optimistic update — mirrors the Redux `pending` handler
    onMutate: async (payload) => {
      const key = favoritesKeys.all(customerId ?? '');
      await queryClient.cancelQueries({ queryKey: key });
      const previousFavorites = queryClient.getQueryData<FavoritesState>(key);

      queryClient.setQueryData<FavoritesState>(key, (old) => {
        const state: FavoritesState = old
          ? { products: { ...old.products }, designs: { ...old.designs } }
          : { products: {}, designs: {} };

        const { itemId, type, isFavorite, itemAdditionalData } = payload;

        if (type === 'product') {
          if (isFavorite) {
            delete state.products[itemId];
          } else {
            state.products[itemId] = { itemId, type, isFavorite: true, ...itemAdditionalData };
          }
        } else if (type === 'design') {
          if (isFavorite) {
            delete state.designs[itemId];
          } else {
            state.designs[itemId] = { itemId, type, isFavorite: true, ...itemAdditionalData };
          }
        }

        return state;
      });

      return { previousFavorites };
    },

    // Rollback on failure — mirrors the Redux `rejected` handler
    onError: (_error, payload, context) => {
      const key = favoritesKeys.all(customerId ?? '');
      if (context?.previousFavorites !== undefined) {
        queryClient.setQueryData(key, context.previousFavorites);
      }
      console.error('[useFavorites] toggle failed for item:', payload.itemId);
    },

    // Always re-sync with server after settle
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: favoritesKeys.all(customerId ?? '') });
    },
  });

  return {
    /** Normalized map of favorited products: { [itemId]: FavoriteItem } */
    products: favorites.products,
    /** Normalized map of favorited designs: { [itemId]: FavoriteItem } */
    designs: favorites.designs,
    /** True while the initial fetch is in flight */
    loading: query.isLoading,
    /** Error from the fetch, if any */
    error: query.error,
    /** Trigger a favorite toggle — optimistic, with rollback on failure */
    toggleFavorite: mutation.mutate,
    /** Async version that returns a promise */
    toggleFavoriteAsync: mutation.mutateAsync,
  };
}
